'use client';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputLabel from '@mui/material/InputLabel';
import Menu from '@mui/material/Menu';
import Paper from '@mui/material/Paper';
import { type Theme, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import type { PickerSelectionState } from '@mui/x-date-pickers/internals';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import dayjs from '@/libs/dayjs';
import { SelectedDay } from './SelectedDay';

type Props = {
  theme: Theme;
  dates: Dayjs[] | null;
  anchorEl: HTMLElement | null;
  onOpen: (el: HTMLElement) => void;
  onClose: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onClickDay: (
    day: Dayjs | null,
    selectionState?: PickerSelectionState
  ) => void;
  setSelectedMonth: (newMonth: Dayjs) => void;
  onClickClear: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onClickThisMonth: () => void;
};

const Component: React.FC<Props> = ({
  theme,
  dates,
  anchorEl,
  onOpen,
  onClose,
  onKeyDown,
  onClickDay,
  setSelectedMonth,
  onClickClear,
  onClickThisMonth
}) => {
  const open = Boolean(anchorEl);
  return (
    <>
      <InputLabel htmlFor="searchbox-date" sx={theme.typography.body2}>
        日付で絞り込む
      </InputLabel>
      <TextField
        id="searchbox-date"
        value={dates?.map((date) => date.format('YYYY/M/D')).join('~')}
        fullWidth
        size="small"
        placeholder="日付を選択"
        onClick={(e) => onOpen(e.currentTarget)}
        inputProps={{ readOnly: true }}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-haspopup="true"
        aria-expanded={open}
      />
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        MenuListProps={{
          sx: { p: 0 },
          'aria-labelledby': 'searchbox-date'
        }}
      >
        <Paper variant="outlined">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              autoFocus
              onChange={onClickDay}
              onMonthChange={(newMonth) => setSelectedMonth(newMonth)}
              onYearChange={(newMonth) => setSelectedMonth(newMonth)}
              slots={{
                day: SelectedDay
              }}
              slotProps={{
                day: {
                  dates
                } as any
              }}
            />
          </LocalizationProvider>
          <Box
            sx={{
              p: 1,
              pt: 0,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1
            }}
          >
            <Button onClick={onClickClear}>選択解除</Button>
            <Button onClick={onClickThisMonth}>この月の全日程を選択</Button>
            <Button onClick={onClose}>閉じる</Button>
          </Box>
        </Paper>
      </Menu>
    </>
  );
};

type OuterProps = {
  dates: Dayjs[];
  setDates: (dates: Dayjs[]) => void;
};

export const DatePicker: React.FC<OuterProps> = ({ dates, setDates }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [lastSelectedDay, setLastSelectedDay] = useState<Dayjs | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null);
  const theme = useTheme();

  const onOpen = (el: HTMLElement) => {
    setAnchorEl(el);
  };

  const onClose = () => {
    setAnchorEl(null);
    setLastSelectedDay(null);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === ' ' || e.key === 'Enter') {
      if (!open) {
        e.preventDefault();
        onOpen(e.currentTarget);
      }
    } else if (
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      if (open) {
        e.preventDefault();
      }
    }
  };

  const onClickDay = (
    day: Dayjs | null,
    selectionState?: PickerSelectionState
  ) => {
    if (!day || selectionState !== 'finish') return;
    const index = dates.findIndex((date) => date.isSame(day, 'day'));
    if (index >= 0) {
      const newDates = dates.filter((_, i) => i !== index);
      newDates.sort((a, b) => a.diff(b));
      setDates(newDates);
    } else {
      // 追加
      let newDates = [...dates];
      if (dates.length === 0) {
        newDates.push(day);
      } else if (dates.length === 1) {
        newDates.push(day);
        setLastSelectedDay(day);
      } else if (dates.length === 2) {
        // 範囲内なら、追加する日とlastSelectedDay
        if (
          day.isAfter(dates[0]) &&
          day.isBefore(dates[1]) &&
          lastSelectedDay
        ) {
          newDates = [day, lastSelectedDay];
        } else {
          newDates = [day];
        }
      }
      newDates.sort((a, b) => a.diff(b));
      setDates(newDates);
      // 2つ目の日付を選択したら、カレンダーを閉じる
      if (newDates.length === 2) {
        setTimeout(() => {
          onClose();
        }, 200);
      }
    }
  };

  const onClickClear = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setDates([]);
  };

  const onClickThisMonth = () => {
    if (!selectedMonth) return;
    const start = selectedMonth.startOf('month');
    const end = selectedMonth.endOf('month');
    setDates([start, end]);
  };

  // 再度開いた時にカレンダー選択月を現在にする
  useEffect(() => {
    if (open) {
      setSelectedMonth(dayjs());
    }
  }, [open]);

  return (
    <Component
      theme={theme}
      dates={dates}
      anchorEl={anchorEl}
      onOpen={onOpen}
      onClose={onClose}
      onKeyDown={onKeyDown}
      onClickDay={onClickDay}
      setSelectedMonth={setSelectedMonth}
      onClickClear={onClickClear}
      onClickThisMonth={onClickThisMonth}
    />
  );
};
